import {
  getEnrollmentFileAuditDetail,
  getEnrollmentFileRecordValidationResult,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import AllstateCard from 'Shared/components/AllstateCard';
import CardField from 'Shared/components/CardField';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {renderAsync} from 'Shared/components/RenderAsync';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {toEither} from 'Core/models/Async';
import {sequenceS} from 'fp-ts/lib/Apply';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import {useParams} from 'react-router';
import 'Enrollment/components/UploadEnrollmentApplications/EnrollmentApplicationFileAuditDetail.sass';

const EnrollmentApplicationFileAuditDetail = () => {
  const dispatch = useDispatch();
  const {fileId} = useParams();
  useBreadcrumbs('Audit Detail', EnrollmentRouteConstants.ENROLLMENT_APPLICATION_FILE_AUDIT_DETAIL(fileId!));

  const {enrollmentFileAuditDetail, enrollmentFileRecordValidationResult} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessing
  );

  useEffect(() => {
    dispatch(getEnrollmentFileAuditDetail(fileId!));
    dispatch(getEnrollmentFileRecordValidationResult(fileId!));
  }, [dispatch, fileId]);

  return pipe(
    sequenceS(E.either)({
      enrollmentFileAuditDetail: toEither(enrollmentFileAuditDetail),
      enrollmentFileRecordValidationResult: toEither(enrollmentFileRecordValidationResult),
    }),
    E.fold(renderAsync, ({enrollmentFileAuditDetail, enrollmentFileRecordValidationResult}) => (
      <div className="enrollment-file-audit">
        <AllstateCard title="Audit Detail">
          <div className="card__content">
            <CardField name="File Name" value={enrollmentFileAuditDetail.fileName} />
            <CardField name="Created Date" value={toFormattedDate(enrollmentFileAuditDetail.createDate!)} />
            <CardField name="Total Records" value={enrollmentFileAuditDetail.totalRecords!.toString()} />
            <CardField name="Status" value={enrollmentFileAuditDetail.fileStatus!.name} />
            <CardField
              name="Account Structure Mismatch"
              value={enrollmentFileAuditDetail.accountStructureMismatches!.toString()}
            />
            <CardField name="Product Mismatch" value={enrollmentFileAuditDetail.productMismatches!.toString()} />
            <CardField name="Invalid SSN" value={enrollmentFileAuditDetail.invalidSSNCount!.toString()} />
            <CardField name="No Match" value={enrollmentFileAuditDetail.policyMismatches!.toString()} />
            <CardField name="Policy Match" value={enrollmentFileAuditDetail.policyMatches!.toString()} />
            <CardField
              name="Coverage Difference"
              value={enrollmentFileAuditDetail.auditCoverageMismatches!.toString()}
            />
            <CardField
              name="Non-Coverage Difference"
              value={enrollmentFileAuditDetail.auditNonCoverageMismatches!.toString()}
            />
            <div>
              <div className="record-validation">
                <h4 id="enrollment-file-audit-record-validation-header" className="record-validation__header">Record Validation</h4>
              </div>
              <div className="coverage-termination">
                <h6 className="coverage-termination__sub-header">
                  Coverage Termination: {enrollmentFileRecordValidationResult.coverageTerminationCount!.toString()}
                </h6>
                <CardField
                  name="Successful"
                  value={enrollmentFileRecordValidationResult.terminationSuccessfulCount!.toString()}
                />
                <CardField
                  name="Unsuccessful"
                  value={enrollmentFileRecordValidationResult.terminationUnSuccessfulCount!.toString()}
                />
              </div>

              <div className="coverage-election">
                <h6 className="coverage-election__sub-header">
                  Coverage Election: {enrollmentFileRecordValidationResult.coverageElectionCount!.toString()}
                </h6>
                <CardField
                  name="Successful"
                  value={enrollmentFileRecordValidationResult.coverageElectionSuccessfulCount!.toString()}
                />
                <CardField
                  name="Unsuccessful"
                  value={enrollmentFileRecordValidationResult.coverageElectionUnsuccessfulCount!.toString()}
                />
              </div>

              <div className="coverage-change">
                <h6 className="coverage-change__sub-header">
                  Coverage Change: {enrollmentFileRecordValidationResult.coverageChangeCount!.toString()}
                </h6>
                <CardField
                  name="Successful"
                  value={enrollmentFileRecordValidationResult.coverageChangeSuccessfulCount!.toString()}
                />
                <CardField
                  name="Unsuccessful"
                  value={enrollmentFileRecordValidationResult.coverageChangeUnSuccessfulCount!.toString()}
                />
              </div>
              <CardField
                name="Reinstatement"
                value={enrollmentFileRecordValidationResult.reinstatementCount!.toString()}
              />
              <div className="non-coverage-demographic-change">
                <h6 className="non-coverage-demographic-change__sub-header">
                  Non-Coverage Demographic Change:{' '}
                  {enrollmentFileRecordValidationResult.nonCoverageDemographicChangeCount!.toString()}
                </h6>
                <CardField
                  name="Successful"
                  value={enrollmentFileRecordValidationResult.nonCoverageDemographicChangeSuccessfulCount!.toString()}
                />
                <CardField
                  name="Unsuccessful"
                  value={enrollmentFileRecordValidationResult.nonCoverageDemographicChangeUnsuccessfulCount!.toString()}
                />
              </div>
              <CardField
                name="Admin Change"
                value={enrollmentFileRecordValidationResult.adminChangeCount!.toString()}
              />
              <CardField
                name="No Changes Detected"
                value={enrollmentFileRecordValidationResult.noChangesDetectedCount!.toString()}
              />
            </div>
          </div>
        </AllstateCard>
      </div>
    ))
  );
};

export default EnrollmentApplicationFileAuditDetail;
